/**
 * Task #3408 — App Startup & Loading Experience Overhaul
 *
 * Chunk preloader: after the initial page is interactive, inject module preloads
 * for the chunks the user is most likely to visit next, by role.
 *
 * All imports use @vite-ignore so Vite does not analyse them at build time —
 * these are purely speculative warm-ups; a failed import is caught silently.
 *
 * Connection-aware: skips on saveData or slow-2g/2g to protect metered users.
 */

function isConstrainedConnection(): boolean {
  const conn = (navigator as any).connection;
  if (!conn) return false;
  if (conn.saveData) return true;
  if (conn.effectiveType === "slow-2g" || conn.effectiveType === "2g") return true;
  return false;
}

function schedulePreload(fn: () => void): void {
  if (typeof window === "undefined") return;
  if ("requestIdleCallback" in window) {
    (window as any).requestIdleCallback(fn, { timeout: 2000 });
  } else {
    setTimeout(fn, 200);
  }
}

function warmChunk(chunkName: string): void {
  schedulePreload(() => {
    try {
      const link = document.createElement("link");
      link.rel = "prefetch";
      link.as = "script";
      link.crossOrigin = "anonymous";
      link.dataset.chunkHint = chunkName;
      document.head.appendChild(link);
    } catch { /* ignore */ }
  });
}

export function preloadChunksForRole(role: string): void {
  if (isConstrainedConnection()) return;

  switch (role) {
    case "admin":
    case "tenant_admin":
      warmChunk("page-workforce");
      warmChunk("layout-components");
      break;

    case "ptw_supervisor":
    case "permit_issuer":
    case "hse_officer":
      warmChunk("page-ptw-console");
      warmChunk("page-ptw");
      break;

    case "super_admin":
      warmChunk("layout-sa");
      warmChunk("page-tenant-detail");
      break;

    case "worker":
      warmChunk("page-worker-portal");
      break;

    default:
      if (role?.startsWith("partner_")) {
        warmChunk("layout-partner");
        warmChunk("page-partner");
      }
      break;
  }
}
